<!-- BulkCartesianForm.vue -->
<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { useUserGroupStore } from "@/stores/useUserGroupStore";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

import SearchUserInput from "./SearchUserInput.vue";
import SearchGroupInput from "./SearchGroupInput.vue";
import ThePagination from "../Generics/ThePagination.vue";

import AddTextLimitModal from "@/components/Text/AddTextLimitModal.vue";
import UpgradePage from "@/components/User/UpgradePage.vue";

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: (value) => ["add", "remove"].includes(value),
  },
});

const emit = defineEmits(["close", "submit"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const userStore = useUserStore();
const groupStore = useGroupStore();
const userGroupStore = useUserGroupStore();

const selectedUserIds = ref([]);
const selectedGroupIds = ref([]);

const userCurrentPage = ref(1);
const groupCurrentPage = ref(1);
const pageSize = ref(10);

const title =
  props.mode === "add"
    ? "Adicionar Vínculos em Lote"
    : "Remover Vínculos em Lote";

const buttonText =
  props.mode === "add" ? "Adicionar Vínculos" : "Remover Vínculos";

const isSubmitting = ref(false);

// TOAST
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao concluir com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveUserPhotoPath(user) {
  if (!user) return null;

  return (
    user.photoPath ||
    user.photoUrl ||
    user.userPhotoPath ||
    user.userPhotoUrl ||
    user.photo_path ||
    user.user_photo_path ||
    null
  );
}

function showErrorToast(message, duration = 3000) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(duration);
}

function showSuccessToast(message, duration = 1500) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(duration);
}

function resetToastTimeout(duration = 1500) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

// MODAL DE LIMITE
const showLimitModal = ref(false);
const limitModalInfo = ref({
  limit: null,
  currentUsage: null,
  newUsage: null,
  message: "",
});

// MODAL DE UPGRADE
const showUpgradeModal = ref(false);

function handleCloseLimitModal() {
  showLimitModal.value = false;
}

function handleOpenPlans() {
  showLimitModal.value = false;
  showUpgradeModal.value = true;
}

function handleCloseUpgrade() {
  showUpgradeModal.value = false;
}

// Contadores
const userCounterText = computed(() => {
  const selected = selectedUserIds.value.length;
  const total = userStore.totalUsersCount ?? 0;
  return `${selected} / ${total}`;
});

const groupCounterText = computed(() => {
  const selected = selectedGroupIds.value.length;
  const total = groupStore.totalGroupsCount ?? 0;
  return `${selected} / ${total}`;
});

// Fetch de usuários e grupos paginados
async function fetchPaginatedUsers() {
  await userStore.fetchUsers(
    userCurrentPage.value,
    pageSize.value,
    userStore.userSearchTerm
  );
}

async function fetchPaginatedGroups() {
  await groupStore.fetchGroupsPaged(
    groupCurrentPage.value,
    pageSize.value,
    groupStore.groupSearchTerm
  );
}

onMounted(async () => {
  await Promise.all([fetchPaginatedUsers(), fetchPaginatedGroups()]);
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

watch(userCurrentPage, fetchPaginatedUsers);
watch(groupCurrentPage, fetchPaginatedGroups);

watch(
  () => userStore.userSearchTerm,
  () => {
    userCurrentPage.value = 1;
    fetchPaginatedUsers();
  }
);

watch(
  () => groupStore.groupSearchTerm,
  () => {
    groupCurrentPage.value = 1;
    fetchPaginatedGroups();
  }
);

async function handleSubmit() {
  if (isSubmitting.value) return;

  const userCount = selectedUserIds.value.length;
  const groupCount = selectedGroupIds.value.length;

  if (userCount === 0 && groupCount === 0) {
    showErrorToast("Selecione pelo menos um usuário e um grupo.");
    return;
  }

  if (userCount === 0) {
    showErrorToast("Selecione pelo menos um usuário.");
    return;
  }

  if (groupCount === 0) {
    showErrorToast("Selecione pelo menos um grupo.");
    return;
  }

  const payload = {
    userIds: [...selectedUserIds.value],
    groupIds: [...selectedGroupIds.value],
  };

  const action = props.mode === "add" ? "adicionados" : "removidos";

  isSubmitting.value = true;

  if (props.mode === "add") {
    try {
      await userGroupStore.bulkAddByCartesian(payload);

      showSuccessToast(`Usuários ${action} com sucesso!`, 1500);
      hideBackdropForSuccess.value = true;
      setTimeout(() => {
        emit("close");
      }, 1500);
    } catch (error) {
      const backendData = error?.response?.data;
      const errorMessage =
        backendData?.detail ||
        backendData?.title ||
        backendData?.message ||
        error?.message ||
        "Ocorreu um erro desconhecido ao tentar adicionar os vínculos.";

      const isLimitError =
        errorMessage.includes("plano atual permite associar") ||
        errorMessage.includes("Nenhum plano ativo encontrado");

      if (isLimitError) {
        limitModalInfo.value = {
          limit: backendData?.limit ?? null,
          currentUsage: backendData?.currentUsage ?? null,
          newUsage: backendData?.newUsage ?? null,
          message: errorMessage,
        };

        showLimitModal.value = true;
        isSubmitting.value = false;
      } else {
        showErrorToast(errorMessage, 3000);
        isSubmitting.value = false;
      }
    }
  } else {
    try {
      await userGroupStore.bulkRemoveByCartesian(payload);

      showSuccessToast(`Vínculos ${action} com sucesso!`, 1500);
      hideBackdropForSuccess.value = true;
      setTimeout(() => {
        emit("close");
      }, 3000);
    } catch (error) {
      const backendData = error?.response?.data;
      const errorMessage =
        backendData?.detail ||
        backendData?.title ||
        backendData?.message ||
        error?.message ||
        "Ocorreu um erro ao tentar remover os vínculos.";

      showErrorToast(errorMessage, 3000);
      isSubmitting.value = false;
    }
  }
}
</script>

<template>
  <div v-if="!showUpgradeModal && !hideBackdropForSuccess" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">{{ title }}</h2>

        <button
          type="button"
          @click="emit('close')"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <p class="modal-description">
          Selecione os usuários e os grupos para
          {{ props.mode === "add" ? "vincular" : "desvincular" }} em massa.
        </p>

        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3"
          style="height: 540px"
        >
          <div class="list-container">
            <div class="list-title">
              <h3>Usuários</h3>
              <span class="counter-tag">{{ userCounterText }}</span>
            </div>

            <div class="search-input-wrapper">
              <SearchUserInput />
            </div>

            <div class="list-scroll">
              <div
                v-for="user in userStore.users"
                :key="user.userId"
                class="list-item"
              >
                <input
                  type="checkbox"
                  :id="`user-${user.userId}`"
                  :value="user.userId"
                  v-model="selectedUserIds"
                  class="checkbox-style"
                />

                <label :for="`user-${user.userId}`" class="user-option-label">
                  <span class="user-avatar">
                    <span
                      v-if="getAvatarUrl(resolveUserPhotoPath(user))"
                      class="user-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveUserPhotoPath(user)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user user-avatar-icon"></i>
                  </span>

                  <span class="user-option-text">
                    <span class="user-option-name">{{ user.userName }}</span>
                    <span class="user-option-email">({{ user.email }})</span>
                  </span>
                </label>
              </div>
            </div>

            <ThePagination
              class="pagination-tight border-t"
              :current-page="userCurrentPage"
              :page-size="pageSize"
              :total-count="userStore.totalUsersCount"
              :total-pages="userStore.totalUsersPages"
              @change-page="(page) => (userCurrentPage = page)"
            />
          </div>

          <div class="list-container">
            <div class="list-title">
              <h3>Grupos</h3>
              <span class="counter-tag">{{ groupCounterText }}</span>
            </div>

            <div class="search-input-wrapper">
              <SearchGroupInput />
            </div>

            <div class="list-scroll">
              <div
                v-for="group in groupStore.groups"
                :key="group.groupId"
                class="list-item"
              >
                <input
                  type="checkbox"
                  :id="`group-${group.groupId}`"
                  :value="group.groupId"
                  v-model="selectedGroupIds"
                  class="checkbox-style"
                />

                <label :for="`group-${group.groupId}`">
                  {{ group.groupName }}
                </label>
              </div>
            </div>

            <ThePagination
              class="pagination-tight border-t"
              :current-page="groupCurrentPage"
              :page-size="pageSize"
              :total-count="groupStore.totalGroupsCount"
              :total-pages="groupStore.totalGroupsPages"
              @change-page="(page) => (groupCurrentPage = page)"
            />
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          @click="emit('close')"
          class="btn btn-secondary"
          :disabled="isSubmitting"
        >
          Cancelar
        </button>

        <button
          v-if="props.mode === 'add'"
          type="button"
          @click="handleSubmit"
          class="btn btn-success"
          :disabled="isSubmitting"
        >
          <svg
            v-if="!isSubmitting"
            class="w-5 h-5 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clip-rule="evenodd"
            />
          </svg>

          <svg
            v-else
            class="w-5 h-5 mr-2 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>

          {{ isSubmitting ? "Adicionando..." : buttonText }}
        </button>

        <button
          v-else
          type="button"
          @click="handleSubmit"
          class="btn btn-danger"
          :disabled="isSubmitting"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddTextLimitModal
      :show="showLimitModal"
      :info="limitModalInfo"
      entityType="usuários"
      @close="handleCloseLimitModal"
      @open-plans="handleOpenPlans"
    />
  </Teleport>

  <Teleport to="body">
    <UpgradePage
      v-if="showUpgradeModal"
      :initial-step="2"
      @close="handleCloseUpgrade"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-content">
            <p class="toast-title">
              {{
                toastType === "error"
                  ? "Não foi possível concluir a operação"
                  : "Operação concluída com sucesso"
              }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 62.5rem;
  height: calc(100vh - 14px - 1.5rem);
  max-height: calc(100vh - 14px - 1.5rem);
}

.modal-description {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.92rem;
}

.modal-footer {
  bottom: 0;
}

/* =========================
   LISTAS
========================= */
.list-container {
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.list-title {
  padding: 0.45rem 0.875rem;
  background-color: var(--bg-header);
  font-weight: 600;
  font-size: 0.92rem;
  border-bottom: 1px solid var(--border-color);

  display: flex;
  justify-content: space-between;
  align-items: center;
}

.counter-tag {
  font-size: 0.72rem;
  background-color: var(--neutral-200);
  color: var(--text-light);
  padding: 0.2rem 0.65rem;
  border-radius: 9999px;
  font-weight: 600;
}

.list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.4rem;
}

.list-item {
  display: flex;
  align-items: center;
  padding: 0.32rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.2rem;
  font-size: 0.9rem;
}

.list-item:hover {
  background-color: var(--surface-2);
}

.list-item label {
  cursor: pointer;
  width: 100%;
  user-select: none;
  margin-left: 0.65rem;
}

/* =========================
   PAGINAÇÃO
========================= */
.pagination-tight {
  padding: 6px !important;
  margin: 6px !important;
}

.pagination-tight :deep(.pagination-container) {
  padding: 2px 0 0 !important;
  margin: 0 !important;
}

.checkbox-style {
  width: 1.05rem;
  flex: 0 0 auto;
}

.search-input-wrapper :deep(.search-input) {
  padding-left: 2.1rem;
}

.search-input-wrapper :deep(.icon-wrapper) {
  padding-left: 1.2rem;
}

/* =========================
   AVATAR NA LISTA DE USUÁRIOS
========================= */
.user-option-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

.user-avatar {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 26px;
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.user-avatar-icon {
  font-size: 0.75rem;
  line-height: 1;
}

.user-option-text {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 0;
  overflow: hidden;
}

.user-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-option-email {
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>